import { ArrowLeft, Send, FileText, CheckCircle2 } from 'lucide-react';
import { Loader } from './ui/Loader';
import { useState, useEffect } from 'react';
import { API_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { PageShell } from './layout/PageShell';
import { ConfirmDialog } from './ConfirmDialog';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';


interface WithdrawalFormPageProps {
  onBack: () => void;
}

export function WithdrawalFormPage({ onBack }: WithdrawalFormPageProps) {
  const { session, userProfile } = useAuth();
  const resolvedUserEmail = session?.user?.email ?? '';
  const resolvedUserName = userProfile?.name ?? '';
  const resolvedAccessToken = session?.access_token ?? null;

  const [formData, setFormData] = useState({
    name: resolvedUserName,
    email: resolvedUserEmail,
    address: '',
    postalCode: '',
    city: '',
    orderDate: '',
    productService: '',
    orderNumber: '',
    message: '',
    deleteAccount: false, // ✅ GDPR: Checkbox for kontosletting
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const { isDirty, markDirty, resetDirty, setDirty } = useDirtyFormGuard(false);

  const updateField = <K extends keyof typeof formData>(key: K, value: (typeof formData)[K]) => {
    markDirty();
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleRestoreDraft = (payload: typeof formData) => {
    if (!payload || typeof payload !== 'object') return;
    setFormData(payload);
    setDirty(true);
  };

  const { clearDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:withdrawal:${session?.user?.id ?? resolvedUserEmail ?? 'anon'}`,
    enabled: !submitted,
    value: formData,
    onRestore: handleRestoreDraft,
  });

  useBeforeUnloadDirty(isDirty && !submitted && !loading);

  const requestBack = () => {
    if (isDirty && !submitted) {
      setShowDiscardConfirm(true);
      return;
    }
    clearDraft();
    resetDirty();
    onBack();
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    clearDraft();
    resetDirty();
    onBack();
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE}/withdrawal/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${resolvedAccessToken}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            address: formData.address,
            postalCode: formData.postalCode,
            city: formData.city,
            orderDate: formData.orderDate,
            productService: formData.productService,
            orderNumber: formData.orderNumber,
            message: formData.message,
            deleteAccount: formData.deleteAccount, // ✅ GDPR: Include kontosletting in the request
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Kunne ikke sende angreskjema');
      }

      clearDraft();
      resetDirty();
      setSubmitted(true);
    } catch (err: any) {
      devError('Error submitting withdrawal:', err);
      setError(err.message || 'En feil oppstod. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PageShell className="pt-page-safe-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 xl:px-8 py-8 mb-16 pb-android-pwa-doc">
          <div className="bg-white rounded-3xl shadow-lg p-8 border-2 border-green-100 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl text-green-900 mb-4">Angring mottatt</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Vi har mottatt din angremelding og vil behandle den så snart som mulig. Du vil motta en bekreftelse på e-post.
            </p>
            <p className="text-gray-700 mb-6">
              Ditt abonnement vil bli kansellert og eventuell refusjon vil bli behandlet innen 14 dager.
            </p>
            {formData.deleteAccount && (
              <p className="text-red-700 mb-6 font-medium">
                Din konto vil også bli permanent slettet i henhold til din forespørsel.
              </p>
            )}
            <button
              onClick={requestBack}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Tilbake til forsiden
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="pt-page-safe-sm">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 xl:px-8 py-8 mb-16 pb-android-pwa-doc">
        <ConfirmDialog
          isOpen={showDiscardConfirm}
          onClose={() => setShowDiscardConfirm(false)}
          onConfirm={confirmDiscard}
          title="Forkaste angreskjema?"
          message="Du har ulagrede endringer i skjemaet. Vil du forkaste disse og gå tilbake?"
          confirmText="Forkast"
          cancelText="Fortsett utfylling"
          variant="warning"
        />

        <div className="bg-white rounded-3xl shadow-lg p-8 border-2 border-purple-100">
          {/* Back button */}
          <button
            onClick={requestBack}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Tilbake</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Angreskjema
              </h1>
            </div>
            <p className="text-gray-600 leading-relaxed">
              I henhold til norsk angrerettlov har du som forbruker rett til å angre kjøpet innen 14 dager etter at avtalen ble inngått.
            </p>
          </div>

        {/* Important Information */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-200 mb-8">
          <h2 className="text-xl text-purple-900 mb-3">Viktig informasjon</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-purple-600 mt-1">•</span>
              <span>Du har 14 dager på deg til å angre kjøpet fra den dagen du kjøpte abonnementet</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 mt-1">•</span>
              <span>Angreretten gjelder kun for forbrukerkjøp (privatpersoner), ikke bedriftskjøp</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 mt-1">•</span>
              <span>Hvis du allerede har brukt tjenesten, kan vi trekke fra en forholdsmessig andel av beløpet</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 mt-1">•</span>
              <span>Refusjon vil bli utbetalt til samme betalingsmiddel innen 14 dager</span>
            </li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">
                Fullt navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="Ditt fulle navn"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">
                E-postadresse <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="din@epost.no"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 mb-2">
              Adresse <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => updateField('address', e.target.value)}
              className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
              placeholder="Gateadresse"
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">
                Postnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.postalCode}
                onChange={(e) => updateField('postalCode', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="0000"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">
                Poststed <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="By"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">
                Bestillingsdato <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.orderDate}
                onChange={(e) => updateField('orderDate', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">
                Abonnement <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.productService}
                onChange={(e) => updateField('productService', e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
              >
                <option value="">Velg abonnement</option>
                <option value="Pluss-abonnement (149 kr/mnd)">Pluss-abonnement (149 kr/mnd)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 mb-2">
              Ordrenummer/Betalings-ID (valgfritt)
            </label>
            <input
              type="text"
              value={formData.orderNumber}
              onChange={(e) => updateField('orderNumber', e.target.value)}
              className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none"
              placeholder="Finner du i bekreftelsesmailen fra Nets/Nexi"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">
              Ekstra informasjon (valgfritt)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => updateField('message', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none resize-none"
              placeholder="Er det noe du ønsker å fortelle oss om hvorfor du angrer kjøpet?"
            />
          </div>

          {/* ✅ GDPR: Checkbox for kontosletting */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.deleteAccount}
                onChange={(e) => updateField('deleteAccount', e.target.checked)}
                className="mt-1 w-5 h-5 text-purple-600 border-2 border-red-300 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
              />
              <div className="flex-1">
                <span className="text-red-900 font-medium">
                  Jeg ønsker også å slette min konto permanent <span className="text-red-500">*</span>
                </span>
                <p className="text-red-700 text-sm mt-1">
                  Ved å huke av her bekrefter du at du ønsker å slette kontoen din permanent. 
                  All data knyttet til profilen din vil bli slettet, inkludert meldinger, lagrede søk og favoritter. 
                  Denne handlingen kan ikke angres.
                </p>
              </div>
            </label>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Merk:</strong> Ved å sende dette skjemaet bekrefter du at du ønsker å benytte deg av angreretten. 
              Ditt abonnement vil bli kansellert og eventuell refusjon vil bli behandlet innen 14 dager.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size="sm" className="text-white" />
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Send angremelding</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={requestBack}
              className="px-6 py-4 border-2 border-purple-200 text-purple-700 rounded-xl hover:bg-purple-50 transition-all"
            >
              Avbryt
            </button>
          </div>
        </form>

        {/* Alternative Contact */}
        <div className="mt-8 pt-8 border-t-2 border-purple-100">
          <h3 className="text-lg text-purple-900 mb-3">Alternativ angremåte</h3>
          <p className="text-gray-700 mb-2">
            Du kan også sende angremeldingen på e-post til:
          </p>
          <p className="text-purple-600 font-medium mb-3">
            kontakt@talentfunn.no
          </p>
          <p className="text-gray-600 text-sm">
            Husk å inkludere navn, adresse, ordrenummer og hvilken tjeneste du ønsker å angre.
          </p>
        </div>
        </div>
      </div>
    </PageShell>
  );
}
